import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "preact/hooks";
import type { RollRecord } from "../rolls";
import { summarizeRoll } from "../rolls";
import type { Entry } from "../../shared/snapshot-format";
import type { Character, Override } from "../character";
import { closeSession, deleteCharacter, getCharacter, getSetting, materialiseGrants, playSessionAction, putCharacter, renameCharacter, reverseLogEntry, setSetting, shareCharacter, startSession } from "../character";
import { pruneSpellcasting } from "../character/spells";
import { loadEngineCatalogue } from "../data/config";
import type { LinkResolver } from "../data/links";
import { loadLinkResolver } from "../data/links";
import type { DerivedSheet } from "../engine";
import type { GuideChange, GuideContext } from "../guide";
import { advanceLevel, applyChange, buildGuideContext, guideLevels, setLevel } from "../guide";
import { ensureMigrated, loadMigrationTarget, markMigrationsSeen } from "../migration";
import type { PlayAction, SessionMaxima, TableAction } from "../session";
import { Builder, type PendingChange } from "./builder";
import { DeleteButton } from "./characters";
import type { ActorContext } from "./enrich";
import { actorContext } from "./enrich-context";
import { EntryViewer, type ViewerRequest } from "./entry-viewer";
import { FamiliarPanel } from "./familiar";
import { Feats } from "./feats";
import { InventoryPanel } from "./inventory";
import { IWREditor } from "./iwr-editor";
import { Library } from "./library";
import { MigrationReport } from "./migration-report";
import { Overview } from "./overview";
import { RollLog } from "./roll-log";
import { RollPanel } from "./roll-panel";
import type { RollPlan } from "./roll-spec";
import { planRoll, requestForKey } from "./roll-spec";
import type { RollOptions, RollRequest } from "./rolls";
import { navigate, type LibrarySection } from "./router";
import { appEngine, describeParkFix, describeParkReason, timeStamp } from "../sync";
import { SessionTab } from "./session-tab";
import { SheetContext, type OpenEntryOptions } from "./sheet-context";
import { Skills } from "./skills";
import { Spells } from "./spells";
import type { StatDetail } from "./stat-detail";
import { statisticDetail } from "./stat-detail";
import { StatPanel } from "./stat-panel";
import { Strikes } from "./strikes";
import { SyncIndicator, useConflictCopies, useParked } from "./sync-status";
import { Tabs, type TabSpec } from "./tabs";
import { ThemeControl } from "./theme-control";
import { UpdateIndicator } from "./update-indicator";
import { VariantRulesPanel } from "./variant-rules";

/** Where a statistic's Override lives: the Character's list or the Familiar's. */
export interface OverrideScope {
  overrides: Override[];
  save: (next: Override[]) => Promise<void>;
}

interface OpenPanel {
  detail: StatDetail;
  scope: OverrideScope;
  dc: number | null;
}

/**
 * One Character (issue #14): the tab shell with Overview, Skills, Feats, Strikes, Spells (#10),
 * Inventory (#11), Familiar (#13), Library (#15), Session (#18), Roll Log (#20) and Build (#9, #12); the statistic panel with
 * breakdowns and Overrides; the Entry viewer; the Roll button hooks (#19). The current tab and
 * each tab's scroll position survive rotation and a reload.
 */
export function CharacterPage({ id }: { id: string }) {
  const [character, setCharacter] = useState<Character | null>();
  const [sheet, setSheet] = useState<DerivedSheet>();
  const [maxima, setMaxima] = useState<SessionMaxima | null>(null);
  const [guide, setGuide] = useState<GuideContext>();
  const [pending, setPending] = useState<PendingChange | null>(null);
  const [entries] = useState(() => new Map<string, Entry>());
  const [busy, setBusy] = useState(false);
  const [legacy, setLegacy] = useState(false);
  const [message, setMessage] = useState<string>();
  const [links, setLinks] = useState<LinkResolver | null>(null);
  const [viewer, setViewer] = useState<ViewerRequest | null>(null);
  const [panel, setPanel] = useState<OpenPanel | null>(null);
  const [tab, setTab] = useState<string>(() => readTab(id));
  const [librarySection, setLibrarySection] = useState<LibrarySection>("browse");
  const [libraryOpened, setLibraryOpened] = useState(false);
  const [menu, setMenu] = useState(false);
  const [focus, setFocus] = useState<string | null>(null);
  const [rollState, setRollState] = useState<{ plan: RollPlan; physical: boolean; key: number } | null>(null);
  const [iwrOpen, setIwrOpen] = useState(false);
  // Sync (#26): the version of this Character the Account holds that this Device cannot apply
  // yet, and whether what is open is itself the losing half of a conflict.
  const waiting = useParked().find((row) => row.collection === "character" && row.id === id);
  const copyHere = useConflictCopies().find((copy) => copy.id === id);
  const scrollMemory = useRef(new Map<string, number>());
  const rollCounter = useRef(0);
  /** The latest Character, for Play Actions queued while an earlier one settles. */
  const characterRef = useRef<Character | null | undefined>(undefined);
  const playQueue = useRef<Promise<void>>(Promise.resolve());
  characterRef.current = character;

  /**
   * Every Character shown has its grants reconciled first (issue #6) and its progression Choices
   * opened (#9): the class features, the background's feat and everything they grant become
   * Selections, the Level's slots appear, and the sheet is derived from the result. A Character
   * either step changed is saved.
   */
  const settle = async (input: Character): Promise<void> => {
    try {
      // A Character saved under another Snapshot is migrated first (#17), unless a Session is open on it.
      const migrated = await ensureMigrated(input, await loadMigrationTarget(), guideSettle);
      if (migrated.refused) setMessage(migrated.refused);
      const stored = migrated.character;
      const m = await materialiseGrants(stored, entries);
      const g = buildGuideContext(m.character, m.entries, await loadEngineCatalogue());
      // Spell state of Entries the sheet no longer derives (a dedication retrained away) is dropped with the rest of the settle.
      const pruned = pruneSpellcasting(g.character, m.sheet.spellcasting.map((e) => e.id));
      const next = m.changed || g.changed || pruned !== g.character ? await putCharacter(pruned) : g.character;
      setCharacter(next);
      setSheet(m.sheet);
      setMaxima(m.maxima);
      setGuide({ ...g, character: next });
    } catch (e) {
      setCharacter(input);
      setMessage(`Could not derive the sheet: ${(e as Error).message}`);
    }
  };

  /** The guide's settle: reconcile and derive without saving, so a change can be previewed. */
  const guideSettle = async (c: Character): Promise<GuideContext> => {
    const m = await materialiseGrants(c, entries);
    return buildGuideContext(m.character, m.entries, await loadEngineCatalogue());
  };

  useEffect(() => {
    setCharacter(undefined);
    setSheet(undefined);
    setGuide(undefined);
    setPending(null);
    setPanel(null);
    setViewer(null);
    setTab(readTab(id));
    // A change from another Device that was held while this Character was in play, or waiting
    // for this Device's Snapshot, is applied before it opens (#25).
    appEngine()
      .resumeParked(id)
      .catch(() => undefined)
      .then(() => getCharacter(id))
      .then((c) => (c ? settle(c) : setCharacter(null)), (e: unknown) => setMessage(String(e)));
    getSetting("showLegacy").then((on) => setLegacy(on === true), () => undefined);
    void setSetting("lastCharacterId", id).catch(() => undefined);
    loadLinkResolver().then(setLinks, () => setLinks(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Keep the current tab across reloads, and each tab's scroll position across tab switches and rotation.
  const switchTab = (next: string) => {
    scrollMemory.current.set(tab, window.scrollY);
    setTab(next);
    try {
      sessionStorage.setItem(`tab:${id}`, next);
    } catch {
      /* private mode: the tab is kept in memory only */
    }
  };
  useLayoutEffect(() => {
    window.scrollTo(0, scrollMemory.current.get(tab) ?? 0);
  }, [tab]);

  const actor: ActorContext | null = useMemo(() => (sheet && character ? actorContext(sheet, character) : null), [sheet, character]);
  const openEntry = useCallback((uuid: string, options: OpenEntryOptions = {}) => setViewer({ uuid, ...options }), []);

  if (character === undefined) return <p class="muted page-message">{message ?? "Loading…"}</p>;
  if (character === null) {
    return (
      <section class="page-message">
        <p class="notice">There is no Character with this id.</p>
        <a href="#/">← Characters</a>
      </section>
    );
  }

  const save = async (next: Character) => {
    try {
      await settle(await putCharacter(next));
    } catch (e) {
      setMessage((e as Error).message);
    }
  };
  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    setMessage(undefined);
    try {
      await work();
    } catch (e) {
      setMessage((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const onRename = () => {
    setMenu(false);
    const name = prompt("Rename the Character", character.name)?.trim();
    if (name) void save(renameCharacter(character, name));
  };
  const onExport = async () => {
    setMenu(false);
    try {
      const outcome = await shareCharacter(character, __APP_VERSION__);
      setMessage(outcome === "downloaded" ? "Saved a Character file." : outcome === "shared" ? "Shared." : undefined);
    } catch (e) {
      setMessage((e as Error).message);
    }
  };
  const onDelete = async () => {
    await deleteCharacter(character.id);
    navigate({ page: "home" });
  };
  /** The migration report (#17) is shown until dismissed; a reopened Choice opens in the Builder. */
  const onReportDismiss = () => run(async () => {
    const seen = markMigrationsSeen(character);
    if (seen !== character) setCharacter(await putCharacter(seen));
  });
  const onReportChoice = (itemId: string) => {
    void onReportDismiss();
    switchTab("build");
    setFocus(itemId);
  };

  /* ------------------------------ statistics and Rolls ------------------------------ */

  const characterScope: OverrideScope = { overrides: character.overrides, save: (next) => save({ ...character, overrides: next }) };
  const onStat = (detail: StatDetail, dc: number | null = null) => setPanel({ detail, scope: characterScope, dc });
  const onFamiliarStat = (detail: StatDetail, scope: OverrideScope) => setPanel({ detail, scope, dc: null });
  /** A Roll (#19): plan it from the sheet and the Session, roll it in the panel; a long press asks for the physical dice. */
  const onRoll = (request: RollRequest, options: RollOptions = {}) => {
    if (!sheet) return;
    setRollState({ plan: planRoll(sheet, character, request), physical: options.physical === true, key: rollCounter.current++ });
  };
  /** A finished Roll is a Play Action with what it did to the Session; without a Session it is shown only. */
  const onRollCommit = (record: RollRecord, effects: TableAction[]) => {
    if (!characterRef.current?.session) return;
    void onPlay({ kind: "roll", roll: record, effects }, summarizeRoll(record));
  };
  /** The statistic panel's Roll button, for keys the sheet rolls. */
  const panelRoll = panel && sheet ? requestForKey(sheet, panel.detail.key, panel.detail.label) : null;

  /* ------------------------------ the Builder ------------------------------ */

  /** A guide change: applied and settled off to the side; committed at once unless it reopens Choices, which the user confirms first. */
  const onGuideChange = (change: GuideChange, label: string) =>
    run(async () => {
      if (!guide) return;
      const outcome = await applyChange(guide, change, guideSettle);
      if (outcome.blocked) {
        setMessage(outcome.blocked);
        return;
      }
      if (outcome.reopened.length > 0) {
        setPending({ label, outcome });
        return;
      }
      await save(outcome.ctx.character);
    });
  const onConfirm = () =>
    run(async () => {
      if (!pending) return;
      const next = pending.outcome.ctx.character;
      setPending(null);
      await save(next);
    });
  const onAdvance = () =>
    run(async () => {
      if (!guide) return;
      const next = await advanceLevel(guide, guideSettle);
      await save(next.character);
    });
  const onBack = () =>
    run(async () => {
      if (!guide || guide.character.level <= 1) return;
      const next = await setLevel(guide, (guide.character.level - 1) as Character["level"], guideSettle);
      await save(next.character);
    });
  const onLegacy = (on: boolean) => {
    setLegacy(on);
    void setSetting("showLegacy", on);
  };

  /* ------------------------------ the Session ------------------------------ */

  /**
   * A Play Action: applied, settled, saved, and written to the Roll Log (#20). Actions queue,
   * each reading the Character the one before it left, so a Roll committed while its follow-up
   * is already open cannot be lost.
   */
  const queue = (work: () => Promise<void>): Promise<void> => {
    const step = async () => {
      setBusy(true);
      setMessage(undefined);
      try {
        await work();
      } catch (e) {
        setMessage((e as Error).message);
      } finally {
        setBusy(false);
      }
    };
    playQueue.current = playQueue.current.then(step, step);
    return playQueue.current;
  };
  const onPlay = (action: PlayAction, label: string): Promise<void> =>
    queue(async () => {
      const current = characterRef.current;
      if (!current) return;
      const r = await playSessionAction(current, action, entries);
      if (r.blocked) {
        setMessage(`${label}: ${r.blocked}`);
        return;
      }
      const saved = await putCharacter(r.character);
      characterRef.current = saved;
      setCharacter(saved);
      setSheet(r.sheet);
      setMaxima(r.maxima);
    });
  /** Reverse a Roll Log entry with its dependents (the Roll Log tab confirms those first); Undo on the Session tab is the newest entry. */
  const onReverse = (id: string): Promise<void> =>
    queue(async () => {
      const current = characterRef.current;
      if (!current) return;
      const r = await reverseLogEntry(current, id, entries);
      const saved = r.reversed.length > 0 ? await putCharacter(r.character) : current;
      characterRef.current = saved;
      setCharacter(saved);
      setSheet(r.sheet);
      setMaxima(r.maxima);
      if (r.blocked) setMessage(`Could not reverse: ${r.blocked}`);
    });
  const onStart = async () => {
    setBusy(true);
    try {
      const opened = await startSession(character);
      const saved = await putCharacter(opened.character);
      setCharacter(saved);
      setSheet(opened.sheet);
      setMaxima(opened.maxima);
    } catch (e) {
      setMessage((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const onClose = async () => {
    await save(closeSession(character));
  };
  /** A toggle switched while a Session is open belongs to the Session; otherwise it is saved on the Character. */
  const onToggle = async (key: string, state: { checked?: boolean; selection?: string }) => {
    if (character.session) return onPlay({ kind: "toggle", key, state: { ...(character.session.toggles[key] ?? {}), ...state } }, `toggle ${key}`);
    const next = { ...(character.toggles[key] ?? {}), ...state };
    await save({ ...character, toggles: { ...character.toggles, [key]: next } });
  };

  /* ------------------------------ the tabs ------------------------------ */

  const sessionOpen = character.session !== null;
  const openChoices = guide ? guideLevels(guide).reduce((n, l) => n + l.open, 0) : 0;
  const tabs: TabSpec[] = [
    { id: "overview", label: "Overview" },
    { id: "skills", label: "Skills" },
    { id: "feats", label: "Feats" },
    { id: "strikes", label: "Strikes" },
    { id: "spells", label: "Spells", badge: sheet ? sheet.spellcasting.reduce((n, e) => n + e.choices.filter((c) => !c.answered).length, 0) || null : null },
    { id: "inventory", label: "Inventory" },
    ...(sheet?.familiar ? [{ id: "familiar", label: "Familiar" }] : []),
    { id: "library", label: "Library" },
    { id: "session", label: "Session", badge: sessionOpen ? "●" : null },
    { id: "log", label: "Roll Log", badge: sessionOpen && character.rollLog.entries.length > 0 ? character.rollLog.entries.length : null },
    { id: "build", label: "Build", badge: openChoices > 0 ? openChoices : null },
  ];
  const activeTab = tabs.some((t) => t.id === tab) ? tab : "overview";
  // The Library mounts once it is first opened, so the search index is not fetched for every Character shown.
  if (activeTab === "library" && !libraryOpened) setLibraryOpened(true);
  const className = sheet?.items.find((i) => i.type === "class")?.name;
  const ancestryName = sheet?.items.find((i) => i.type === "ancestry")?.name;

  return (
    <SheetContext.Provider value={{ openEntry, roll: onRoll }}>
      <div class="sheet">
        <header class="sheet-header">
          <a href="#/" class="icon back" aria-label="Characters">‹</a>
          <div class="sheet-title">
            <h1>{character.name}</h1>
            <p class="muted">
              Level {character.level}{ancestryName ? ` ${ancestryName}` : ""}{className ? ` ${className}` : ""}{sessionOpen ? " · in play" : ""}
            </p>
          </div>
          <button type="button" class="icon" aria-label="Character menu" aria-expanded={menu} onClick={() => setMenu(!menu)}>⋯</button>
          {menu && (
            <div class="menu card" role="menu">
              <button type="button" role="menuitem" onClick={onRename}>Rename</button>
              <button type="button" role="menuitem" onClick={() => void onExport()}>Export…</button>
              <DeleteButton name={character.name} onConfirm={() => void onDelete()} />
              <div class="menu-theme"><ThemeControl /></div>
              <p class="muted">Snapshot {character.snapshotTag} · app {__APP_VERSION__}</p>
              <UpdateIndicator />
              <SyncIndicator link={false} />
            </div>
          )}
        </header>
        <Tabs tabs={tabs} active={activeTab} onSelect={switchTab} />
        {copyHere && (
          <p class="notice sheet-notice" role="status">
            This is a <strong>Conflict Copy</strong> of “{copyHere.name}”, from {copyHere.device}, {timeStamp(copyHere.at)}. The version that kept the
            Account is <a href={`#/c/${encodeURIComponent(copyHere.of)}`}>{copyHere.name}</a>; the Character list can swap the two.
          </p>
        )}
        {waiting && (
          <p class="notice sheet-notice" role="status">
            A newer version of this Character is waiting on the Account: {describeParkReason(waiting.reason)}. {describeParkFix(waiting.reason)}
          </p>
        )}
        {message && (
          <p class="notice sheet-notice" role="status">
            {message} <button type="button" class="linkish" onClick={() => setMessage(undefined)}>dismiss</button>
          </p>
        )}
        <main class="sheet-body" data-tab={activeTab}>
          {!sheet ? <p class="muted">Deriving…</p> : (
            <>
              <div hidden={activeTab !== "overview"}><Overview character={character} sheet={sheet} maxima={maxima} busy={busy} onStat={onStat} onToggle={onToggle} onIWR={() => setIwrOpen(true)} /></div>
              <div hidden={activeTab !== "skills"}><Skills sheet={sheet} onStat={onStat} /></div>
              <div hidden={activeTab !== "feats"}><Feats sheet={sheet} /></div>
              <div hidden={activeTab !== "strikes"}><Strikes sheet={sheet} onStat={onStat} /></div>
              <div hidden={activeTab !== "spells"}>
                <Spells character={character} entries={sheet.spellcasting} busy={busy} disabled={sessionOpen ? "A Session is open: spells known change with the build; preparations stay open." : null} legacy={legacy} onSave={save} onMessage={setMessage} />
              </div>
              <div hidden={activeTab !== "inventory"}>
                <InventoryPanel character={character} view={sheet.inventory} busy={busy} legacy={legacy} sessionOpen={sessionOpen} onSave={save} onMessage={setMessage} />
              </div>
              {sheet.familiar && (
                <div hidden={activeTab !== "familiar"}>
                  <FamiliarPanel character={character} sheet={sheet} ctx={guide} busy={busy} legacy={legacy} sessionOpen={sessionOpen} encounter={character.session?.round !== null && sessionOpen} onChange={onGuideChange} onSave={save} onStat={onFamiliarStat} />
                </div>
              )}
              <div hidden={activeTab !== "library"}>
                {libraryOpened && <Library section={librarySection} onSection={setLibrarySection} legacy={legacy} onLegacy={onLegacy} />}
              </div>
              <div hidden={activeTab !== "session"}>
                <SessionTab character={character} sheet={sheet} maxima={maxima} busy={busy} log={character.rollLog} onPlay={onPlay} onUndo={onReverse} onLog={() => switchTab("log")} onStart={onStart} onClose={onClose} />
              </div>
              <div hidden={activeTab !== "log"}>
                <RollLog character={character} busy={busy} actor={actor} links={links} onReverse={onReverse} />
              </div>
              <div hidden={activeTab !== "build"}>
                {guide ? (
                  <>
                    <Builder ctx={guide} busy={busy} disabled={sessionOpen ? "A Session is open: close it before changing the build." : null} legacy={legacy} onLegacy={onLegacy}
                      onChange={onGuideChange} onAdvance={onAdvance} onBack={onBack} pending={pending} onConfirm={onConfirm} onCancel={() => setPending(null)} focus={focus} onFocused={() => setFocus(null)} />
                    <div class="card">
                      <VariantRulesPanel ctx={guide} busy={busy} disabled={sessionOpen ? "A Session is open: close it before changing the Variant Rules." : null} settle={guideSettle} onSave={save} onMessage={setMessage} />
                    </div>
                  </>
                ) : (
                  <p class="muted">Preparing the Builder…</p>
                )}
              </div>
            </>
          )}
        </main>
        {!panel && !viewer && <MigrationReport character={character} busy={busy} onDismiss={() => void onReportDismiss()} onOpenChoice={onReportChoice} />}
        {iwrOpen && sheet && <IWREditor character={character} sheet={sheet} busy={busy} onSave={async (iwr) => { setIwrOpen(false); await save({ ...character, iwr }); }} onClose={() => setIwrOpen(false)} />}
        {panel && <StatPanel detail={panel.detail} overrides={panel.scope.overrides} busy={busy} dc={panel.dc} onOverrides={async (next) => { setPanel(null); await panel.scope.save(next); }} onClose={() => setPanel(null)} onRoll={panelRoll ? (options) => { setPanel(null); onRoll(panelRoll, options); } : undefined} />}
        <EntryViewer request={viewer} links={links} actor={actor} onRoll={onRoll} onClose={() => setViewer(null)} />
        {rollState && <RollPanel key={rollState.key} plan={rollState.plan} physical={rollState.physical} session={character.session} maxima={maxima} actor={actor} links={links} busy={busy} onCommit={onRollCommit} onFollow={(request) => onRoll(request)} onClose={() => setRollState(null)} />}
      </div>
    </SheetContext.Provider>
  );
}

function readTab(id: string): string {
  try {
    return sessionStorage.getItem(`tab:${id}`) ?? "overview";
  } catch {
    return "overview";
  }
}
